import React from 'react';
import { IconButton, Typography } from '@mui/material';
import { Delete } from '@mui/icons-material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { Controller, useWatch } from 'react-hook-form';
import { PhoneNumber } from '@product-framework/components/FormFields';
import { TextField, Switch, Radio } from '@ui-framework/index';
import { observer } from 'mobx-react';

function ContactRow({
  idx,
  control,
  controlName,
  remove,
  update,
  fields,
  setValue,
  permissions,
  headers,
}) {
  const row = useWatch({
    control,
    name: `${controlName}.${idx}`,
  });

  if (!row) return null;

  const updateOtherFields = type => {
    fields.forEach((_, i) => {
      if (i !== idx) {
        setValue(`${controlName}.${i}.${type}`, false);
      }
    });
  };

  // Ensure only one "isVoiceDefault" or "isTextDefault" selected at a time
  const updateRow = (patch, operation) => {
    update(idx, { ...row, ...patch });
    if (operation?.type) {
      updateOtherFields(operation?.type);
    }
  };

  return (
    <Grid2 container>
      <Grid2 xs md={0.5} textAlign="center" sx={{ p: 0.4 }}>
        {permissions?.[controlName]?.phoneNumber === 'WRITE' ? (
          <IconButton size="small" color="error" onClick={() => remove(idx)}>
            <Delete />
          </IconButton>
        ) : (
          <Typography>&nbsp;</Typography>
        )}
      </Grid2>
      <Grid2 xs md={headers.phoneNumber.width.md} sx={{ p: 0.4 }}>
        <Controller
          control={control}
          name={`${controlName}.${idx}.phoneNumber`}
          render={({ field }) => (
            <PhoneNumber
              value={field.value}
              smsSubscriptionStatus={row?.smsSubscriptionStatus || null}
              field={{
                name: `${controlName}.${idx}.phoneNumber`,
                type: 'INSIGHT_NUMBER_ONLY',
                dependencies: {
                  insightPurchased: 'insightPurchased',
                },
              }}
              onChange={field.onChange}
              permission={permissions?.[controlName]?.phoneNumber}
            />
          )}
        />
      </Grid2>
      <Grid2 xs md={headers.description.width.md} sx={{ p: 0.4 }}>
        <Controller
          control={control}
          name={`${controlName}.${idx}.description`}
          render={({ field }) => (
            <TextField
              field={{ name: `${controlName}.${idx}.description` }}
              {...field}
              permission={permissions?.[controlName]?.description}
            />
          )}
        />
      </Grid2>

      <Grid2
        xs
        md={headers.isVoiceEnabled.width.md}
        textAlign="center"
        sx={{
          backgroundColor: headers.isVoiceEnabled?.backgroundColor,
          p: 0.4,
        }}>
        <Switch
          checked={row.isVoiceEnabled}
          overwriteProps={{
            onChange: e => {
              if (e.target.checked) {
                updateRow({
                  isVoiceEnabled: true,
                });
              } else {
                updateRow({
                  isVoiceEnabled: false,
                  isVoiceDefault: false,
                });
              }
            },
          }}
          name={`${controlName}.${idx}.isVoiceEnabled`}
          field={{ name: `${controlName}.${idx}.isVoiceEnabled` }}
          permission={permissions?.[controlName]?.isVoiceEnabled}
        />
      </Grid2>

      <Grid2
        xs
        md={headers.isVoiceDefault.width.md}
        textAlign="center"
        sx={{
          backgroundColor: headers.isVoiceDefault?.backgroundColor,
          p: 0.4,
        }}>
        <Radio
          checked={row.isVoiceDefault}
          onChange={() =>
            updateRow(
              {
                isVoiceDefault: true,
                isVoiceEnabled: true,
              },
              { type: 'isVoiceDefault' },
            )
          }
          name={`${controlName}.${idx}.isVoiceDefault`}
          field={{ name: `${controlName}.${idx}.isVoiceDefault` }}
          permission={permissions?.[controlName]?.isVoiceDefault}
        />
      </Grid2>

      <Grid2
        xs
        md={headers.isTextEnabled.width.md}
        textAlign="center"
        sx={{
          backgroundColor: headers.isTextEnabled?.backgroundColor,
          p: 0.4,
        }}>
        <Switch
          checked={row.isTextEnabled}
          overwriteProps={{
            onChange: e => {
              if (e.target.checked) {
                updateRow({
                  isTextEnabled: true,
                });
              } else {
                updateRow({
                  isTextEnabled: false,
                  isTextDefault: false,
                });
              }
            },
          }}
          name={`${controlName}.${idx}.isTextEnabled`}
          field={{ name: `${controlName}.${idx}.isTextEnabled` }}
          permission={permissions?.[controlName]?.isTextEnabled}
        />
      </Grid2>
      <Grid2
        xs
        md={headers.isTextDefault.width.md}
        textAlign="center"
        sx={{
          backgroundColor: headers.isTextDefault?.backgroundColor,
          p: 0.4,
        }}>
        <Radio
          checked={row.isTextDefault}
          onChange={() =>
            updateRow(
              {
                isTextDefault: true,
                isTextEnabled: true,
              },
              { type: 'isTextDefault' },
            )
          }
          name={`${controlName}.${idx}.isTextDefault`}
          field={{ name: `${controlName}.${idx}.isTextDefault` }}
          permission={permissions?.[controlName]?.isTextDefault}
        />
      </Grid2>
    </Grid2>
  );
}

export default observer(ContactRow);
